import { appStore } from '@store/appStore.ts'
import { useCallback, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  DEFAULT_SORT, EngagementListHeader, type StatusFilter, type StatusPill,
} from "./components/EngagementListHeader.tsx"
import { EngagementListTable } from "./components/EngagementListTable.tsx"
import CreateEngagement from "./components/CreateEngagement.tsx"
import "../../engagementsList.scss"

const ALL_PILLS: StatusPill[] = ["OPEN", "CLOSED"];

function parseStatusParam(param: string | null): StatusFilter {
  if (!param) return new Set<StatusPill>(["OPEN"]);
  const pills = param.split(",").filter((p): p is StatusPill => ALL_PILLS.includes(p as StatusPill));
  return pills.length > 0 ? new Set(pills) : new Set<StatusPill>(["OPEN"]);
}

function serializeStatus(filter: StatusFilter): string | null {
  // Default (just OPEN) → omit from URL
  if (filter.size === 1 && filter.has("OPEN")) return null;
  return [...filter].join(",");
}

function parseList(param: string | null): string[] {
  return param ? param.split(",").filter(Boolean) : [];
}

function parseIds(param: string | null): number[] {
  return parseList(param).map(Number).filter((n) => Number.isFinite(n));
}

/**
 * Live / Not Live, as the API's tri-state.
 *
 * Both ticked and neither ticked mean the same thing -- show everything -- so both send no
 * parameter at all. Sending `live=true&live=false` would be a filter that reads as narrower
 * than it is.
 */
function liveParam(selected: string[]): boolean | undefined {
  if (selected.length !== 1) return undefined;
  return selected[0] === "live";
}

export const EngagementsList = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [total, setTotal] = useState(0)

  const selectedStatus = parseStatusParam(searchParams.get("status"))

  const selectedManagerIds = parseIds(searchParams.get("manager_id"))
  const managerUnassigned = searchParams.get("manager_unassigned") === "true"
  const selectedCsmIds = parseIds(searchParams.get("csm_id"))
  const csmUnassigned = searchParams.get("csm_unassigned") === "true"
  const mine = searchParams.get("mine") === "true"
  const selectedLive = parseList(searchParams.get("live"))
  const sort = searchParams.get("sort") ?? DEFAULT_SORT

  const selectedTypes = parseList(searchParams.get("type"))
  // In the URL so an internal user can link someone straight to the archived view.
  const archived = searchParams.get("archived") === "true"
  const searchTerm = searchParams.get("search") ?? ""

  const updateParams = useCallback((updates: Record<string, string | null>) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)
      for (const [key, value] of Object.entries(updates)) {
        if (value == null) next.delete(key)
        else next.set(key, value)
      }
      return next
    }, { replace: true })
  }, [setSearchParams])

  const handleStatusChange = useCallback((status: StatusFilter) => {
    updateParams({ status: serializeStatus(status), offset: null })
  }, [updateParams])

  const handleManagerChange = useCallback((ids: number[], unassigned: boolean) => {
    updateParams({
      manager_id: ids.length ? ids.join(",") : null,
      manager_unassigned: unassigned ? "true" : null,
      offset: null,
    })
  }, [updateParams])

  const handleCsmChange = useCallback((ids: number[], unassigned: boolean) => {
    updateParams({
      csm_id: ids.length ? ids.join(",") : null,
      csm_unassigned: unassigned ? "true" : null,
      offset: null,
    })
  }, [updateParams])

  const handleMineChange = useCallback((next: boolean) => {
    updateParams({ mine: next ? "true" : null, offset: null })
  }, [updateParams])

  const handleLiveChange = useCallback((values: string[]) => {
    updateParams({ live: values.length ? values.join(",") : null, offset: null })
  }, [updateParams])

  const handleSortChange = useCallback((next: string) => {
    // The default is omitted so a shared URL carries only what somebody actually chose.
    updateParams({ sort: next === DEFAULT_SORT ? null : next, offset: null })
  }, [updateParams])

  const handleTypesChange = useCallback((types: string[]) => {
    updateParams({ type: types.length ? types.join(",") : null, offset: null })
  }, [updateParams])

  const handleSearchChange = useCallback((term: string) => {
    updateParams({ search: term.trim() ? term : null, offset: null })
  }, [updateParams])

  const handleArchivedChange = useCallback((next: boolean) => {
    // Offset reset because the two sets are unrelated: page 3 of the live list is not
    // page 3 of the archived one, and landing on an empty page reads as a broken filter.
    //
    // My Engagements is cleared with it. The archived view is a small set somebody is
    // looking through deliberately, and carrying a personal filter into it silently is how
    // "I archived it and now it is gone" happens.
    updateParams({ archived: next ? "true" : null, mine: null, offset: null })
  }, [updateParams])

  const canArchive = appStore.loggedInUser?.permissions?.hasSomeAccess?.("Platform Admin") ?? false

  return (
    <div className="eng-list">
      <CreateEngagement />
      <EngagementListHeader
        selectedManagerIds={selectedManagerIds}
        managerUnassigned={managerUnassigned}
        onManagerChange={handleManagerChange}
        selectedCsmIds={selectedCsmIds}
        csmUnassigned={csmUnassigned}
        onCsmChange={handleCsmChange}
        selectedStatus={selectedStatus}
        onStatusChange={handleStatusChange}
        selectedTypes={selectedTypes}
        onTypesChange={handleTypesChange}
        selectedLive={selectedLive}
        onLiveChange={handleLiveChange}
        mine={mine}
        onMineChange={handleMineChange}
        sort={sort}
        onSortChange={handleSortChange}
        searchTerm={searchTerm}
        onSearchChange={handleSearchChange}
        archived={archived}
        onArchivedChange={handleArchivedChange}
        canArchive={canArchive}
        total={total}
      />
      <EngagementListTable
        managerIds={selectedManagerIds}
        managerUnassigned={managerUnassigned}
        csmIds={selectedCsmIds}
        csmUnassigned={csmUnassigned}
        mine={mine}
        live={liveParam(selectedLive)}
        statusFilter={selectedStatus}
        types={selectedTypes}
        search={searchTerm}
        archived={archived}
        onTotalChange={setTotal}
      />
    </div>
  )
}
